import { useState, useCallback, useEffect } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AppRoutes } from "./router";
import { DesktopRoutes } from "./router/DesktopRoutes";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import SplashScreen from "./components/feature/SplashScreen";
import { useDeviceType } from "./hooks/useDeviceType";
import { ThemeProvider, useTheme } from "./hooks/useTheme";
import { LanguageProvider } from "./hooks/useLanguage";
import { ToastProvider } from "./components/base/Toast";
import BottomNav from "./components/feature/BottomNav";
import Header from "./pages/home/components/Header";
import AddressDateTimeBar from "./pages/home/components/AddressDateTimeBar";
import InstallPrompt from "./components/feature/InstallPrompt";
import AdminBar from "./components/feature/AdminBar";
import { registerSW } from "./utils/registerSW";
import { Suspense, Component, ReactNode } from "react";
import PageLoadingFallback from "./components/feature/PageLoadingFallback";
import { useAuthListener } from "./hooks/useAuthListener";
import { VendorRouteGuard, isVendorUser } from "./lib/vendorGuard";
import { CourierRouteGuard, isCourierUser } from "./lib/courierGuard";

// Global Error Boundary for catching route crashes
class RouteErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode },
  { hasError: boolean; errorMsg: string }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, errorMsg: '' };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMsg: error.message };
  }
  componentDidCatch(error: Error) {
    console.error('Route crashed:', error);
  }
  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="min-h-[60dvh] flex flex-col items-center justify-center px-6">
          <i className="ri-error-warning-line text-4xl text-amber-500 mb-3"></i>
          <p className="text-stone-600 text-sm mb-4">خطا در بارگذاری صفحه</p>
          <button
            onClick={() => { this.setState({ hasError: false, errorMsg: '' }); window.location.reload(); }}
            className="px-5 py-2.5 rounded-full bg-amber-500 text-white text-sm font-semibold cursor-pointer"
          >
            تلاش مجدد
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function MobileApp() {
  const location = useLocation();
  const { isDark } = useTheme();
  const isVendorSession =
    typeof window !== 'undefined' && isVendorUser(localStorage.getItem('userType'));
  const isCourierSession =
    typeof window !== 'undefined' && isCourierUser(localStorage.getItem('userType'));
  const hideBottomNav =
    isVendorSession ||
    isCourierSession ||
    location.pathname === '/login' ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/vendor') ||
    location.pathname.startsWith('/courier') ||
    location.pathname.startsWith('/story-studio');
  const hideHeader =
    isVendorSession ||
    isCourierSession ||
    location.pathname === '/login' ||
    location.pathname === '/profile' ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/vendor') ||
    location.pathname.startsWith('/courier') ||
    location.pathname.startsWith('/story-studio') ||
    location.pathname.startsWith('/campaign/');

  return (
    <div
      className={`min-h-dvh lg:hidden transition-colors duration-500 ${
        isDark
          ? 'bg-gradient-to-br from-dark via-dark-100 to-[#1A1614]'
          : 'bg-gradient-to-br from-ivory via-cream to-sand'
      }`}
    >
      <AdminBar />
      {!hideHeader && <Header isDarkMode={isDark} />}
      {!hideHeader && <AddressDateTimeBar isDarkMode={isDark} inlineBar={false} />}
      <div className="lg:pt-0">
        <Suspense fallback={<PageLoadingFallback />}>
          <RouteErrorBoundary>
            <AppRoutes />
          </RouteErrorBoundary>
        </Suspense>
      </div>
      {!hideBottomNav && <BottomNav isDarkMode={isDark} />}
      <InstallPrompt />
    </div>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const isDesktop = useDeviceType();

  const handleSplashFinish = useCallback(() => {
    setShowSplash(false);
  }, []);

  useEffect(() => {
    registerSW();
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <LanguageProvider>
        <ThemeProvider>
          <ToastProvider>
            <BrowserRouter basename={__BASE_PATH__}>
              <AppWithSplash
                isDesktop={isDesktop}
                showSplash={showSplash}
                onSplashFinish={handleSplashFinish}
              />
            </BrowserRouter>
          </ToastProvider>
        </ThemeProvider>
      </LanguageProvider>
    </I18nextProvider>
  );
}

function AppWithSplash({
  isDesktop,
  showSplash,
  onSplashFinish,
}: {
  isDesktop: boolean;
  showSplash: boolean;
  onSplashFinish: () => void;
}) {
  const { isDark } = useTheme();

  useAuthListener();

  return (
    <div className={isDark ? 'bg-dark' : 'bg-ivory'}>
      <VendorRouteGuard />
      <CourierRouteGuard />
      {/* Desktop routes render without header transition */}
      {isDesktop && <DesktopRoutes />}

      {/* Mobile app with persistent header */}
      {!isDesktop && <MobileApp />}

      {/* Splash screen overlays everything until loading completes */}
      <AnimatePresence>
        {showSplash && (
          <SplashScreen onFinish={onSplashFinish} isDarkMode={isDark} />
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;